import {
  AwaitContainer,
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Item,
  ItemActions,
  ItemDescription,
  ItemHeader,
  ItemTitle,
  Search,
} from '@ibsys/kit';
import { TruncatedText } from '@/shared/ui';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeesInfinite } from '@/entities/employee';
import type { EmployeeListItem } from '@/entities/conversations/model/api/types';
import { getFullName } from '@/shared/lib';

type Props = {
  existingEmployeeIds: number[];
  onCancel: () => void;
  onSave: (employees: EmployeeListItem[]) => void;
  className?: string;
};

/**
 * Диалог добавления операторов в группу. Ограниченная выгрузка операторов (limit: 50)
 * с пагинацией, локальная фильтрация по ФИО, множественный выбор.
 * Уже состоящие в группе операторы исключены из доступных.
 */
export const AddOperatorsDialog: FC<Props> = ({ existingEmployeeIds, onCancel, onSave, className }) => {
  const { t } = useTranslation(['common', 'employees']);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data, isLoading } = useEmployeesInfinite();
  const allEmployees: EmployeeListItem[] = useMemo(() => data?.pages.flatMap((p) => p.employees) ?? [], [data]);

  const availableEmployees = useMemo(
    () => allEmployees.filter((employee) => !existingEmployeeIds.includes(employee.id)),
    [allEmployees, existingEmployeeIds],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return availableEmployees;
    const q = search.toLowerCase();
    return availableEmployees.filter((employee) => {
      const fio = getFullName(employee.firstName, employee.lastName, employee.middleName).toLowerCase();
      return fio.includes(q) || (employee.externalId ?? '').toLowerCase().includes(q);
    });
  }, [availableEmployees, search]);

  const handleToggle = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const handleSave = () => {
    onSave(availableEmployees.filter((employee) => selectedIds.includes(employee.id)));
  };

  const formatFio = (employee: EmployeeListItem) =>
    getFullName(employee.firstName, employee.lastName, employee.middleName);

  return (
    <DialogContent className={className}>
      <DialogHeader>
        <DialogTitle>{t('employees:groups.addOperators')}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('employees:groups.searchOperatorPlaceholder')}
        />

        <AwaitContainer isLoading={isLoading}>
          <div className="flex flex-col gap-3 max-h-120 overflow-auto">
            {filtered.map((employee) => (
              <Item variant="outline" size="sm" key={employee.id}>
                <ItemHeader>
                  <ItemTitle>{formatFio(employee)}</ItemTitle>
                  <ItemActions className="ml-4">
                    <Checkbox value={selectedIds.includes(employee.id)} onClick={() => handleToggle(employee.id)} />
                  </ItemActions>
                </ItemHeader>
                {employee.externalId && (
                  <ItemDescription className="text-sm text-muted-foreground">
                    <TruncatedText>{`${t('employees:externalId')}: ${employee.externalId}`}</TruncatedText>
                  </ItemDescription>
                )}
              </Item>
            ))}
            {filtered.length === 0 && <div className="text-muted-foreground">{t('employees:groups.notFound')}</div>}
          </div>
        </AwaitContainer>
      </div>

      <DialogFooter>
        <div className="flex gap-3 justify-end w-full">
          <Button variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave} disabled={selectedIds.length === 0}>
            {t('common:add')}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
